import { DataTable } from "@bcc/components/data-table";
import type { DataTableRow } from "@bcc/components/data-table/data-table.types";
import { ORDER_COLUMN_IDS } from "@bcc/components/orders/constants";
import { getOrderColumns } from "@bcc/components/orders/orders-data-table/orders-columns";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { PurchaseOrder } from "@shared/types";
import type { OnChangeFn, Row, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { setBatchOrdersTableContainerTestId } from "./batch-orders-table.utils";

interface BatchOrdersTableProps {
  readonly purchaseOrders: PurchaseOrder[];
  readonly onOrderClick: (order: PurchaseOrder) => void;
  readonly rowSelection?: RowSelectionState;
  readonly onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  readonly testId?: string;
  readonly areRowsDisabled?: boolean;
}

const COLUMN_VISIBILITY = {
  [ORDER_COLUMN_IDS.CREATED_AT_STRING]: false,
  [ORDER_COLUMN_IDS.CREATED_AT_WITH_TIME]: false,
  [ORDER_COLUMN_IDS.EXPECTED_FULFILLMENT_DATE]: false,
  [ORDER_COLUMN_IDS.BATCH]: false,
  [ORDER_COLUMN_IDS.SHIPPING_ADDRESS]: false,
};

export function BatchOrdersTable({
  purchaseOrders,
  onOrderClick,
  rowSelection,
  onRowSelectionChange,
  testId,
  areRowsDisabled,
}: BatchOrdersTableProps) {
  const { t } = useTranslation();
  const [{ currentVendor, customerAccountCarrierDetails }] = useVendorContext();
  // Mirrors the products table's ship-to cell: only visible for cross-border
  // vendors. `shipTo` always includes the vendor's own home country, so a
  // length of 1 means domestic-only — cross-border requires 2+.
  const showShipToFlags = (currentVendor?.properties?.shipTo?.length ?? 0) > 1;

  const rows = useMemo<DataTableRow<PurchaseOrder>[]>(
    () =>
      purchaseOrders.map((order) => ({
        ...order,
        id: order.purchaseOrderId,
        isDisabled: areRowsDisabled ?? false,
      })),
    [purchaseOrders, areRowsDisabled]
  );

  const columns = useMemo(
    () =>
      getOrderColumns({
        onOrderClick,
        onBatchClick: undefined,
        carrierDetails: customerAccountCarrierDetails ?? [],
        t,
        showShipToFlags,
      }),
    [customerAccountCarrierDetails, onOrderClick, t, showShipToFlags]
  );

  const handleRowClick = useCallback(
    (row: Row<DataTableRow<PurchaseOrder>>) => {
      onOrderClick(row.original);
    },
    [onOrderClick]
  );

  const selectable = Boolean(rowSelection !== undefined && onRowSelectionChange);

  return (
    <div data-testid={testId ?? setBatchOrdersTableContainerTestId()}>
      <DataTable
        columns={columns}
        data={rows}
        columnVisibility={COLUMN_VISIBILITY}
        emptyMessage={t("views.orders.emptyState")}
        onRowClick={handleRowClick}
        selectable={selectable}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
      />
    </div>
  );
}
